import { getSiloV3MarketEntry, siloV3LenderKey } from '@1delta/data-sdk'
import type { AdditionalYields } from '../../../types'
import type { GenericTokenList } from '../../../types/general'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { createMarketUid } from '../../../utils'
import type { SiloApiEntry } from '../silo-v2/fetchPublic'

/**
 * Silo v3 GraphQL → public-data converter. Mirrors the v2 converter
 * (`silo-v2/convertPublic.ts`) exactly — the only differences are the
 * protocol-version filter (`v3`), the registry lookup
 * (`getSiloV3MarketEntry`) and the synthesized lender key
 * (`SILO_V3_<HEX>`). The API payload shape is identical for both versions,
 * so we reuse the `SiloApiEntry` / `SiloApiMarket` types from the v2
 * module rather than duplicating them.
 */
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

export function convertSiloV3MarketsToPublicResponse(
  apiItems: SiloApiEntry[],
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokenList: GenericTokenList = {},
): { [lender: string]: any } {
  const out: { [lender: string]: any } = {}

  for (const item of apiItems) {
    if (item.protocol && item.protocol.protocolVersion !== 'v3') continue

    let lenderKey: string
    try {
      lenderKey = siloV3LenderKey(item.configAddress)
    } catch {
      continue
    }

    const registryEntry = getSiloV3MarketEntry(chainId, lenderKey)
    if (!registryEntry) continue

    const [apiSide0, apiSide1] =
      item.market1.index === 0
        ? [item.market1, item.market2]
        : [item.market2, item.market1]

    const sides = [
      { api: apiSide0, self: registryEntry.silo0, other: registryEntry.silo1 },
      { api: apiSide1, self: registryEntry.silo1, other: registryEntry.silo0 },
    ]

    const data: { [marketUid: string]: any } = {}

    for (const { api, self, other } of sides) {
      const decimals = api.inputToken?.decimals ?? self.decimals
      const tokenAddrLc = (api.inputTokenId ?? self.token).toLowerCase()

      // The indexer's `supply` aggregates Collateral + Protected. Only
      // Collateral backs borrows, so utilization / liquidity math reads
      // the Collateral-only fields (matches the on-chain path's
      // `Silo.totalAssets()`). Fallbacks cover markets where the indexer
      // omits the explicit fields.
      const totalDeposits =
        api.collateralAssetsSupply != null
          ? safeNumber(api.collateralAssetsSupply)
          : safeNumber(api.supply)
      const totalDebt = safeNumber(api.borrowed)
      const totalLiquidity =
        api.liquidity != null
          ? safeNumber(api.liquidity)
          : Math.max(totalDeposits - totalDebt, 0)
      const utilization =
        api.utilization != null
          ? safeNumber(api.utilization)
          : totalDeposits > 0
            ? totalDebt / totalDeposits
            : 0

      // Indexer returns rates already as percent (e.g. "38.91" = 38.91 %).
      const variableBorrowRatePct = safeNumber(api.borrowRate)
      const depositRatePct = safeNumber(api.depositRate)

      const assetMeta = tokenList[tokenAddrLc]
      const oracleKey =
        toOracleKey(assetMeta?.assetGroup ?? null) ||
        toGenericPriceKey(tokenAddrLc, chainId)
      const fallbackPrice = prices[oracleKey] ?? 0
      const totalDepositsUSD =
        api.collateralAssetsSupplyUsd != null && api.collateralAssetsSupplyUsd > 0
          ? api.collateralAssetsSupplyUsd
          : api.supplyUsd != null && api.supplyUsd > 0
            ? api.supplyUsd
            : totalDeposits * fallbackPrice
      const totalDebtUSD =
        api.borrowedUsd != null && api.borrowedUsd > 0
          ? api.borrowedUsd
          : totalDebt * fallbackPrice
      const totalLiquidityUSD =
        api.liquidityUsd != null && api.liquidityUsd > 0
          ? api.liquidityUsd
          : Math.max(totalDepositsUSD - totalDebtUSD, 0)

      // selfLt > 0 → this asset can be used as collateral.
      // otherLt > 0 → counterpart can serve as collateral, so this asset is
      // borrowable. A side with lt == 0 is the borrow-only half of an
      // asymmetric pair.
      const otherApi = sides[0].api === api ? apiSide1 : apiSide0
      const selfLt = bigintToLtv(api.lt ?? self.lt)
      const selfMaxLtv = bigintToLtv(api.maxLtv ?? self.maxLtv)
      const otherLt = bigintToLtv(otherApi.lt ?? other.lt)

      // `liquidationFee` is the seize penalty, WAD-scaled (1e18 = 100%) —
      // same scale as lt/maxLtv. Normalize to a plain fraction.
      const selfLiquidationPenalty = bigintToLtv(
        api.liquidationFee ?? self.liquidationFee,
      )

      const collateralActive = selfLt > 0
      const borrowingEnabled = otherLt > 0

      const siloAddrLc = self.silo.toLowerCase()
      const marketUid = createMarketUid(chainId, lenderKey, siloAddrLc)

      data[marketUid] = {
        marketUid,
        name: item.name
          ? `Silo V3 ${item.name} ${assetMeta?.symbol ?? api.inputToken?.symbol ?? ''}`
          : `Silo V3 ${assetMeta?.symbol ?? api.inputToken?.symbol ?? ''}`,
        poolId: siloAddrLc,
        underlying: tokenAddrLc,
        asset: {
          ...(assetMeta ?? {}),
          chainId,
          decimals,
          address: tokenAddrLc,
          symbol:
            assetMeta?.symbol ?? api.inputToken?.symbol ?? self.symbol ?? '',
          name: assetMeta?.name ?? api.inputToken?.symbol ?? self.symbol ?? '',
        },
        totalDeposits,
        totalDebt,
        totalDebtStable: 0,
        totalLiquidity,
        borrowLiquidity: totalLiquidity,
        totalDepositsUSD,
        totalDebtUSD,
        totalDebtStableUSD: 0,
        totalLiquidityUSD,
        borrowLiquidityUSD: totalLiquidityUSD,
        utilization,
        depositRate: depositRatePct,
        variableBorrowRate: variableBorrowRatePct,
        stableBorrowRate: 0,
        intrinsicYield:
          additionalYields?.intrinsicYields?.[assetMeta?.assetGroup as any] ??
          0,
        rewards: undefined,
        decimals,
        config: {
          0: {
            category: 0,
            borrowCollateralFactor: selfMaxLtv,
            collateralFactor: selfLt,
            borrowFactor: 1,
            liquidationPenalty: selfLiquidationPenalty,
            closeFactor: 1,
            collateralDisabled: !collateralActive,
            debtDisabled: !borrowingEnabled,
          },
        },
        closeFactor: 1,
        collateralActive,
        borrowingEnabled,
        depositsEnabled: true,
        hasStable: false,
        isActive: true,
        isFrozen: false,
        params: {
          metadata: {
            silo: self.silo.toLowerCase(),
            counterpartySilo: other.silo.toLowerCase(),
            siloConfig: registryEntry.siloConfig.toLowerCase(),
            oracle: (
              api.solvencyOracleAddress ||
              self.solvencyOracle ||
              ZERO_ADDRESS
            ).toLowerCase(),
            irm: (
              api.interestRateModelId ||
              self.interestRateModel ||
              ZERO_ADDRESS
            ).toLowerCase(),
            shareTokens: {
              collateral: self.collateralShareToken,
              protected: self.protectedShareToken,
              debt: self.debtShareToken,
            },
            fees: {
              dao: api.daoFee ?? self.daoFee,
              deployer: api.deployerFee ?? self.deployerFee,
              liquidation: api.liquidationFee ?? self.liquidationFee,
              flashloan: api.flashLoanFee ?? self.flashloanFee,
            },
          },
        },
      }
    }

    out[lenderKey] = { data, chainId }
  }

  return out
}

function safeNumber(v: string | number | null | undefined): number {
  if (v == null) return 0
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

function bigintToLtv(v: string | null | undefined): number {
  if (!v) return 0
  try {
    return Number(BigInt(v)) / 1e18
  } catch {
    const n = Number(v)
    return Number.isFinite(n) ? n / 1e18 : 0
  }
}

